import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Users, Network, History, UserPlus, Loader2, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const fmtINR = (n) => "₹" + new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

export default function SubBrokerDashboard() {
  const { api, user } = useAuth();
  const [w, setW] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("mapped_clients");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    (async () => {
      try {
        const [wd, ls] = await Promise.all([
          api.get("/subbroker/dashboard/widgets"),
          user?.role === "super_admin" || user?.role === "broker" || user?.role === "platform_owner"
            ? api.get("/subbroker/list").catch(() => ({ data: { subbrokers: [] } }))
            : Promise.resolve({ data: { subbrokers: [] } }),
        ]);
        setW(wd.data);
        setList(ls.data.subbrokers || []);
      } finally { setLoading(false); }
    })();
  }, [api, user]);

  const isAdmin = user?.role === "super_admin" || user?.role === "broker" || user?.role === "platform_owner";

  const onSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir(col === "mapped_clients" ? "desc" : "asc"); }
  };
  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 text-gray-300" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-teal-600" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-teal-600" />;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cmp = (a, b) => {
      const av = a?.[sortBy], bv = b?.[sortBy];
      if (typeof av === "number" || typeof bv === "number") return (Number(av) || 0) - (Number(bv) || 0);
      return String(av || "").localeCompare(String(bv || ""), undefined, { sensitivity: "base" });
    };
    const sorted = [...list].sort((a, b) => (sortDir === "asc" ? cmp(a, b) : cmp(b, a)));
    if (!q) return sorted;
    return sorted.filter((r) =>
      (r.sub_broker_code || "").toLowerCase().includes(q) ||
      (r.sub_broker_name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.status || "").toLowerCase().includes(q)
    );
  }, [list, query, sortBy, sortDir]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="subbroker-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Associate Dashboard</h1>
        <p className="text-sm text-gray-500">
          {isAdmin
            ? "Manage your associates. Client mapping and assignment now lives under Client Wise."
            : "Your mapped clients & portfolio at a glance"}
        </p>
      </div>

      {isAdmin ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI label="Total Associates" value={list.length} icon={Network} color="text-teal-600 bg-teal-50" />
            <KPI label="Total Client Mappings" value={w?.total_mappings || 0} icon={Users} color="text-indigo-600 bg-indigo-50" />
            <KPI label="Active Associates" value={list.filter((r) => r.status === "active").length} icon={UserPlus} color="text-emerald-600 bg-emerald-50" />
            <KPI label="Inactive / Pending" value={list.filter((r) => r.status !== "active").length} icon={History} color="text-gray-600 bg-gray-50" />
          </div>

          <Card className="p-5" data-testid="associate-list-card">
            <div className="flex items-center justify-between mb-4 gap-3">
              <div>
                <h3 className="font-semibold text-gray-800">All Associates</h3>
                <p className="text-xs text-gray-500">{filtered.length} of {list.length} shown — click any column header to sort</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search code · name · email · status"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 w-80"
                  data-testid="associate-search"
                />
              </div>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-gray-500 sticky top-0 bg-white z-10">
                  <tr>
                    <th className="text-left py-2 px-2 w-12">#</th>
                    <th className="text-left py-2 px-2 cursor-pointer select-none hover:text-teal-700" onClick={() => onSort("sub_broker_code")} data-testid="sort-code">
                      Associate Code<SortIcon col="sub_broker_code" />
                    </th>
                    <th className="text-left py-2 px-2 cursor-pointer select-none hover:text-teal-700" onClick={() => onSort("sub_broker_name")} data-testid="sort-name">
                      Name<SortIcon col="sub_broker_name" />
                    </th>
                    <th className="text-left py-2 px-2 cursor-pointer select-none hover:text-teal-700" onClick={() => onSort("email")} data-testid="sort-email">
                      Email<SortIcon col="email" />
                    </th>
                    <th className="text-left py-2 px-2 cursor-pointer select-none hover:text-teal-700" onClick={() => onSort("status")} data-testid="sort-status">
                      Status<SortIcon col="status" />
                    </th>
                    <th className="text-right py-2 px-2 cursor-pointer select-none hover:text-teal-700" onClick={() => onSort("mapped_clients")} data-testid="sort-clients">
                      Linked Clients<SortIcon col="mapped_clients" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`associate-row-${r.sub_broker_code}`}>
                      <td className="py-2 px-2 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="py-2 px-2 font-mono text-xs">
                        <Link to={`/client-wise?associate=${r.sub_broker_code}`} className="text-teal-700 hover:underline">
                          {r.sub_broker_code}
                        </Link>
                      </td>
                      <td className="py-2 px-2">{r.sub_broker_name}</td>
                      <td className="py-2 px-2 text-gray-500 text-xs">{r.email || "—"}</td>
                      <td className="py-2 px-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          r.status === "active" ? "bg-emerald-50 text-emerald-700"
                          : r.status === "pending" ? "bg-amber-50 text-amber-700"
                          : "bg-gray-100 text-gray-600"
                        }`}>
                          {r.status || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums">{r.mapped_clients || 0}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-gray-400 py-8">No associates match your filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="My Clients" value={w?.total_clients || 0} icon={Users} color="text-teal-600 bg-teal-50" />
          <KPI label="Current AUM" value={fmtINR(w?.current_aum)} icon={Network} color="text-emerald-600 bg-emerald-50" big />
          <KPI label="Active SIPs" value={w?.active_sips || 0} icon={UserPlus} color="text-indigo-600 bg-indigo-50" />
          <KPI label="Net Flow" value={fmtINR(w?.net_flow)} icon={History} color="text-gray-600 bg-gray-50" big />
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, big }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
      </div>
      <p className={big ? "text-xl font-bold text-gray-800" : "text-3xl font-bold text-gray-800"}>{value}</p>
    </Card>
  );
}
